import { useEffect } from "react";
import { FaChevronRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchComments, fetchQueries, fetchFyCount, fetchTotalEntities } from "../store/action/overview";
import { useNavigate } from "react-router-dom";
import { normalizePath, normalizeString } from "../utils/pathUtils";
import moment from "moment";

export default function OverviewSection() {
    const dispatch = useDispatch();
    const { comments, queries, openCount, fyCount, totalMails, totalEntities } = useSelector(
        (state) => state.overview
    );

    useEffect(() => {
        dispatch(fetchComments());
        dispatch(fetchQueries());
        dispatch(fetchFyCount());
        dispatch(fetchTotalEntities());
    }, [dispatch]);

    return (
        <div className="mb-5">
            <OverviewHeader
                totalEntities={totalEntities}
                openCount={openCount}
                totalFinancialYears={fyCount}
                unreadEmails={totalMails}
            />

            <DashboardSection
                comments={comments}
                queries={queries}
                totalFinancialYears={fyCount}
            />
        </div>
    );
}

const OverviewHeader = ({
    openCount = 0,
    totalEntities = 0,
    totalFinancialYears = 0,
    unreadEmails = 0,
}) => {
    return (
        <div className="mb-3 rounded p-3">
            <div className="row g-3">
                <div className="col-6 col-md-3">
                    <OverviewHeaderCard Title="Total Open Queries" Value={openCount} />
                </div>
                <div className="col-6 col-md-3">
                    <OverviewHeaderCard Title="Total Entities" Value={totalEntities} />
                </div>
                <div className="col-6 col-md-3">
                    <OverviewHeaderCard Title="Total Financial Years" Value={totalFinancialYears} />
                </div>
                <div className="col-6 col-md-3">
                    <OverviewHeaderCard Title="Unread Emails" Value={unreadEmails} />
                </div>
            </div>
        </div>
    );
};

const OverviewHeaderCard = ({ Title, Value }) => {
    return (
        <div className="card text-center shadow-sm h-100">
            <div className="card-body d-flex flex-column justify-content-between">
                <h6 className="text-muted">{Title}</h6>
                <h2 className="fw-bold mb-0">{Value ?? "0"}</h2>
            </div>
        </div>
    );
};

const DashboardSection = ({ comments, loadingComments, queries, loadingQueries }) => {
    return (
        <div className="row g-3 mb-3">
            <div className="col-12 col-lg-6">
                <LatestComments comments={comments} loading={loadingComments} />
            </div>
            <div className="col-12 col-lg-6">
                <NewQueries queries={queries} loading={loadingQueries} />
            </div>
        </div>
    );
};



const LatestComments = ({ comments, loading }) => {
    const navigate = useNavigate();
    const handleNavigateToComment = (comment) => {
        const { query } = comment;
        if (!query) return;
        const { client, financialYear } = query;
        if (!client || !financialYear) return
        navigate(`${normalizePath(client?.title + ':' + client?.abn)}/${moment(financialYear?.start).format('YYYY')}:${moment(financialYear?.end).format('YYYY')}:${normalizeString(financialYear?.fy_type)}:${normalizeString(financialYear?.period_type)}/${query?.query_id}`);
    };
    const handleNavigateToEmail = (email) => {
        const { fy } = email;
        if (!fy) return;
        const { client } = fy;
        if (!client) return
        navigate(`${normalizePath(client?.title + ':' + client?.abn)}/${moment(fy?.start).format('YYYY')}:${moment(fy?.end).format('YYYY')}:${normalizeString(fy?.fy_type)}:${normalizeString(fy?.period_type)}?tab=email`);
    };

    return (
        <div className="card h-100">
            <div
                className="card-header text-white fw-semibold"
                style={{ backgroundColor: "#1890FF" }}
            >
                Latest Comments and Emails
            </div>
            <div
                className="list-group list-group-flush overflow-auto"
                style={{ maxHeight: "300px" }}
            >
                {loading ? (
                    <div className="list-group-item text-muted">Loading...</div>
                ) : comments.length > 0 ? (
                    comments.map((item, index) => {
                        const isComment = item?.query?._id;
                        return (
                            <div
                                key={item._id}
                                className="list-group-item d-flex justify-content-between align-items-center"
                            >
                                <div
                                    className="flex-grow-1 text-truncate me-3"
                                    style={{ maxWidth: "50%" }}
                                >
                                    <span className="fw-semibold">
                                        {isComment ? "Comment" : "Email"}
                                    </span>{" "}
                                    | {item.text}
                                </div>
                                <div className="d-flex align-items-center">
                                    <small className="text-muted text-nowrap">
                                        {item.created_by?.first_name}{" "}
                                        {item.created_by?.last_name} |{" "}
                                        {new Date(item.createdAt).toLocaleString()}
                                    </small>
                                    <button
                                        className="btn btn-link p-0 ms-2"
                                        onClick={() =>
                                            isComment
                                                ? handleNavigateToComment(item)
                                                : handleNavigateToEmail(item)
                                        }
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="list-group-item text-muted">
                        No comments or emails found
                    </div>
                )}
            </div>
        </div>
    );
};



const NewQueries = ({ queries, loading }) => {
    const navigate = useNavigate();
    // helper function to decide badge color
    const getBadgeClass = (status) => {
        switch (status) {
            case "OPEN":
                return "bg-success"; // green
            case "CLOSED":
                return "bg-danger"; // red
            case "PENDING":
                return "bg-warning text-dark"; // yellow
            default:
                return "bg-secondary"; // gray
        }
    };

    const handleNavigateToQuery = (query) => {
        const { client, financialYear } = query;
        if (!client || !financialYear) return
        navigate(`${normalizePath(client?.title + ':' + client?.abn)}/${moment(financialYear?.start).format('YYYY')}:${moment(financialYear?.end).format('YYYY')}:${normalizeString(financialYear?.fy_type)}:${normalizeString(financialYear?.period_type)}/${query?.query_id}`);
    }

    return (
        <div className="card h-100 shadow-sm">
            <div className="card-header text-white fw-semibold" style={{ backgroundColor: "#1890FF" }}>
                New Queries
            </div>
            <div className="row g-3 p-3">
                {loading ? (
                    <div className="col-12 text-muted">Loading...</div>
                ) : queries.length > 0 ? (
                    queries.map((item, index) => (
                        <div onClick={() => handleNavigateToQuery(item)} key={item._id || index} className="col-12 col-sm-6 col-lg-4" style={{ cursor: "pointer" }}>
                            <div className="border rounded p-3 h-100 shadow-sm">
                                <div className="fw-bold text-truncate">
                                    {item.query_id} : {item.title || "Untitled Query"}
                                </div>
                                <div className="text-muted small">
                                    {item.client?.title || "Entity Name"}
                                </div>
                                <div className="mt-2">
                                    <span className={`badge ${getBadgeClass(item.status)} fw-semibold`}>
                                        {item.status || "Unknown"}
                                    </span>
                                </div>
                                <div className="text-end mt-2">
                                    <button className="btn btn-link p-0 text-primary">
                                        <FaChevronRight />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-12 text-muted">No queries found</div>
                )}
            </div>
        </div>
    );
};